import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaUser, FaCamera, FaHeart, FaCrown, FaEdit, FaSave, FaTimes,
  FaCheckCircle, FaTrash, FaStar, FaUpload, FaShieldAlt, FaExclamationTriangle,
  FaChevronRight, FaCloudUploadAlt, FaUserCircle
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

// Profile Completion Meter
const CompletionMeter = ({ profile }) => {
  const fields = [
    'name', 'age', 'height', 'weight', 'complexion', 'religion', 'caste',
    'qualification', 'occupation', 'income', 'city', 'state', 'about',
    'dateOfBirth', 'maritalStatus', 'motherTongue'
  ];
  const filled = fields.filter(f => profile?.[f]).length;
  const percentage = Math.round((filled / fields.length) * 100);
  const color = percentage >= 80 ? 'bg-green-500' : percentage >= 50 ? 'bg-[#FFD700]' : 'bg-[#DC143C]';

  return (
    <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-700 font-['Noto_Sans_Devanagari']">प्रोफाइल पूर्णता</h3>
        <span className="text-lg font-bold text-[#8B0000]">{percentage}%</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-3">
        <div className={`${color} h-3 rounded-full transition-all duration-500`} style={{ width: `${percentage}%` }} />
      </div>
      {percentage < 80 && (
        <p className="text-xs text-gray-500 mt-2 font-['Noto_Sans_Devanagari']">
          चांगले मॅच मिळवण्यासाठी प्रोफाइल पूर्ण करा
        </p>
      )}
    </div>
  );
};

// Editable Field
const EditableField = ({ label, value, field, editing, onChange, type = 'text', options }) => (
  <div className="py-3 border-b border-gray-50 last:border-0">
    <label className="block text-xs text-gray-500 font-['Noto_Sans_Devanagari'] mb-1">{label}</label>
    {editing ? (
      options ? (
        <select
          value={value || ''}
          onChange={e => onChange(field, e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#DC143C] outline-none"
        >
          <option value="">निवडा</option>
          {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      ) : type === 'textarea' ? (
        <textarea
          value={value || ''}
          onChange={e => onChange(field, e.target.value)}
          rows={3}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#DC143C] outline-none resize-none"
        />
      ) : (
        <input
          type={type}
          value={value || ''}
          onChange={e => onChange(field, e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#DC143C] outline-none"
        />
      )
    ) : (
      <p className="text-sm text-gray-800 font-medium">{value || <span className="text-gray-400 italic">भरलेले नाही</span>}</p>
    )}
  </div>
);

// Tab Button
const TabButton = ({ active, label, icon: Icon, count, onClick }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all whitespace-nowrap ${
      active
        ? 'border-[#8B0000] text-[#8B0000]'
        : 'border-transparent text-gray-500 hover:text-[#8B0000] hover:border-gray-300'
    }`}
  >
    <Icon className="text-sm" />
    <span>{label}</span>
    {count !== undefined && (
      <span className={`text-xs px-1.5 py-0.5 rounded-full ${active ? 'bg-[#8B0000] text-white' : 'bg-gray-200 text-gray-600'}`}>
        {count}
      </span>
    )}
  </button>
);

const MyProfilePage = () => {
  const { user, api, isPremium, updateUser } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [interests, setInterests] = useState({ sent: [], received: [], mutual: [] });
  const [interestsTab, setInterestsTab] = useState('sent');
  const [preferences, setPreferences] = useState({});
  const [editingPrefs, setEditingPrefs] = useState(false);
  const [prefData, setPrefData] = useState({});
  const [subscription, setSubscription] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    if (activeTab === 'interests') fetchInterests();
    if (activeTab === 'subscription') fetchSubscription();
  }, [activeTab]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/profiles/me');
      const data = response.data.profile || response.data;
      setProfile(data);
      setEditData(data);
      setPhotos(data.photos || []);
      setPreferences(data.partnerPreferences || data.preferences || {});
      setPrefData(data.partnerPreferences || data.preferences || {});
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInterests = async () => {
    try {
      const [sentRes, receivedRes, mutualRes] = await Promise.allSettled([
        api.get('/api/interests/sent'),
        api.get('/api/interests/received'),
        api.get('/api/interests/mutual')
      ]);
      setInterests({
        sent: sentRes.status === 'fulfilled' ? (sentRes.value.data.interests || sentRes.value.data || []) : [],
        received: receivedRes.status === 'fulfilled' ? (receivedRes.value.data.interests || receivedRes.value.data || []) : [],
        mutual: mutualRes.status === 'fulfilled' ? (mutualRes.value.data.interests || mutualRes.value.data || []) : []
      });
    } catch (error) {
      console.error('Failed to fetch interests:', error);
    }
  };

  const fetchSubscription = async () => {
    try {
      const response = await api.get('/api/subscriptions/my-plan');
      setSubscription(response.data.subscription || response.data);
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
      setSubscription(null);
    }
  };

  const handleFieldChange = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await api.put('/api/profiles/me', editData);
      const updated = response.data.profile || response.data;
      setProfile(updated);
      setEditData(updated);
      setEditing(false);
      updateUser(updated);
      toast.success('प्रोफाइल यशस्वीरित्या अपडेट केले!');
    } catch (error) {
      toast.error('प्रोफाइल अपडेट करण्यात अयशस्वी');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append('photos', files[i]);
    }

    try {
      const response = await api.post('/api/profiles/photos', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const newPhotos = response.data.photos || response.data;
      setPhotos(Array.isArray(newPhotos) ? newPhotos : [...photos, ...newPhotos]);
      toast.success('फोटो अपलोड केला!');
      fetchProfile();
    } catch (error) {
      toast.error('फोटो अपलोड करण्यात अयशस्वी');
    }
  };

  const handleDeletePhoto = async (photoUrl, index) => {
    try {
      await api.delete('/api/profiles/photos', { data: { photoUrl, index } });
      setPhotos(prev => prev.filter((_, i) => i !== index));
      toast.success('फोटो हटवला!');
    } catch (error) {
      toast.error('फोटो हटवण्यात अयशस्वी');
    }
  };

  const handleSetPrimary = async (photoUrl, index) => {
    try {
      await api.put('/api/profiles/photos/primary', { photoUrl, index });
      const updated = [...photos];
      const [primary] = updated.splice(index, 1);
      updated.unshift(primary);
      setPhotos(updated);
      toast.success('प्राथमिक फोटो सेट केला!');
    } catch (error) {
      toast.error('प्राथमिक फोटो सेट करण्यात अयशस्वी');
    }
  };

  const handleSavePreferences = async () => {
    setSaving(true);
    try {
      await api.put('/api/profiles/preferences', prefData);
      setPreferences(prefData);
      setEditingPrefs(false);
      toast.success('जोडीदार अपेक्षा अपडेट केल्या!');
    } catch (error) {
      toast.error('अपडेट करण्यात अयशस्वी');
    } finally {
      setSaving(false);
    }
  };

  const handlePrefChange = (field, value) => {
    setPrefData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFF8F0]">
        <Navbar />
        <div className="max-w-5xl mx-auto px-4 py-8 animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-48" />
          <div className="h-12 bg-gray-200 rounded" />
          <div className="h-64 bg-gray-200 rounded-xl" />
          <div className="h-64 bg-gray-200 rounded-xl" />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF8F0] flex flex-col">
      <Navbar />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex-1">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full overflow-hidden border-3 border-[#FFD700] shadow-lg flex-shrink-0">
              {photos.length > 0 ? (
                <img src={photos[0]} alt={profile?.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-[#8B0000] to-[#DC143C] flex items-center justify-center">
                  <FaUserCircle className="text-white/50 text-3xl" />
                </div>
              )}
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-[#8B0000] font-['Playfair_Display']">
                {profile?.name || 'My Profile'}
              </h1>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs text-gray-500 font-mono">ID: #{profile?.profileId || profile?._id?.slice(-8)}</span>
                {profile?.isVerified ? (
                  <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                    <FaCheckCircle className="text-[8px]" /> सत्यापित
                  </span>
                ) : (
                  <Link
                    to="/aadhaar-verification"
                    className="bg-yellow-100 text-yellow-700 text-xs px-2 py-0.5 rounded-full flex items-center gap-1 hover:bg-yellow-200"
                  >
                    <FaExclamationTriangle className="text-[8px]" /> पडताळणी करा
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Completion Meter */}
        <CompletionMeter profile={profile} />

        {/* Tabs */}
        <div className="mt-6 border-b border-gray-200 overflow-x-auto">
          <div className="flex">
            <TabButton active={activeTab === 'profile'} label="प्रोफाइल" icon={FaUser} onClick={() => setActiveTab('profile')} />
            <TabButton active={activeTab === 'photos'} label="फोटो" icon={FaCamera} count={photos.length} onClick={() => setActiveTab('photos')} />
            <TabButton active={activeTab === 'preferences'} label="अपेक्षा" icon={FaHeart} onClick={() => setActiveTab('preferences')} />
            <TabButton active={activeTab === 'interests'} label="रस" icon={FaStar} onClick={() => setActiveTab('interests')} />
            <TabButton active={activeTab === 'subscription'} label="सदस्यता" icon={FaCrown} onClick={() => setActiveTab('subscription')} />
          </div>
        </div>

        {/* Tab Content */}
        <div className="mt-6">
          {/* PROFILE TAB */}
          {activeTab === 'profile' && (
            <div className="space-y-5">
              <div className="flex justify-end">
                {editing ? (
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setEditing(false); setEditData(profile); }}
                      className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
                    >
                      <FaTimes /> रद्द करा
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="flex items-center gap-2 px-4 py-2 bg-[#8B0000] text-white rounded-lg text-sm hover:bg-[#DC143C] disabled:opacity-50"
                    >
                      <FaSave /> {saving ? 'सेव्ह करत आहे...' : 'सेव्ह करा'}
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setEditing(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-[#8B0000] text-white rounded-lg text-sm hover:bg-[#DC143C]"
                  >
                    <FaEdit /> संपादित करा
                  </button>
                )}
              </div>

              {/* Personal Info */}
              <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
                <h3 className="text-lg font-semibold text-[#8B0000] font-['Playfair_Display'] mb-4 pb-2 border-b border-gray-100">
                  Personal Information / वैयक्तिक माहिती
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
                  <EditableField label="नाव" value={editing ? editData.name : profile?.name} field="name" editing={editing} onChange={handleFieldChange} />
                  <EditableField label="वय" value={editing ? editData.age : profile?.age} field="age" editing={editing} onChange={handleFieldChange} type="number" />
                  <EditableField label="जन्मतारीख" value={editing ? editData.dateOfBirth : profile?.dateOfBirth} field="dateOfBirth" editing={editing} onChange={handleFieldChange} type="date" />
                  <EditableField label="लिंग" value={editing ? editData.gender : profile?.gender} field="gender" editing={editing} onChange={handleFieldChange} options={['male', 'female']} />
                  <EditableField label="उंची" value={editing ? editData.height : profile?.height} field="height" editing={editing} onChange={handleFieldChange} />
                  <EditableField label="वजन (kg)" value={editing ? editData.weight : profile?.weight} field="weight" editing={editing} onChange={handleFieldChange} type="number" />
                  <EditableField label="वर्ण" value={editing ? editData.complexion : profile?.complexion} field="complexion" editing={editing} onChange={handleFieldChange} options={['गोरा', 'गहू वर्ण', 'सावळा', 'काळा']} />
                  <EditableField label="रक्तगट" value={editing ? editData.bloodGroup : profile?.bloodGroup} field="bloodGroup" editing={editing} onChange={handleFieldChange} options={['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-']} />
                  <EditableField label="वैवाहिक स्थिती" value={editing ? editData.maritalStatus : profile?.maritalStatus} field="maritalStatus" editing={editing} onChange={handleFieldChange} options={['अविवाहित', 'विधवा/विधुर', 'घटस्फोटित', 'विभक्त']} />
                </div>
              </div>

              {/* Community */}
              <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
                <h3 className="text-lg font-semibold text-[#8B0000] font-['Playfair_Display'] mb-4 pb-2 border-b border-gray-100">
                  Community / समाज माहिती
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
                  <EditableField label="धर्म" value={editing ? editData.religion : profile?.religion} field="religion" editing={editing} onChange={handleFieldChange} options={['हिंदू', 'बौद्ध', 'जैन', 'ख्रिश्चन', 'मुस्लिम', 'शीख', 'इतर']} />
                  <EditableField label="जात" value={editing ? editData.caste : profile?.caste} field="caste" editing={editing} onChange={handleFieldChange} />
                  <EditableField label="उपजात" value={editing ? editData.subCaste : profile?.subCaste} field="subCaste" editing={editing} onChange={handleFieldChange} />
                  <EditableField label="गोत्र" value={editing ? editData.gothra : profile?.gothra} field="gothra" editing={editing} onChange={handleFieldChange} />
                  <EditableField label="राशी" value={editing ? editData.rashi : profile?.rashi} field="rashi" editing={editing} onChange={handleFieldChange} options={['मेष', 'वृषभ', 'मिथुन', 'कर्क', 'सिंह', 'कन्या', 'तूळ', 'वृश्चिक', 'धनु', 'मकर', 'कुंभ', 'मीन']} />
                  <EditableField label="मातृभाषा" value={editing ? editData.motherTongue : profile?.motherTongue} field="motherTongue" editing={editing} onChange={handleFieldChange} options={['मराठी', 'हिंदी', 'इंग्रजी', 'कन्नड', 'इतर']} />
                  <EditableField label="कुलदैवत" value={editing ? editData.kuldaivat : profile?.kuldaivat} field="kuldaivat" editing={editing} onChange={handleFieldChange} />
                  <EditableField label="नक्षत्र" value={editing ? editData.nakshatra : profile?.nakshatra} field="nakshatra" editing={editing} onChange={handleFieldChange} />
                </div>
              </div>

              {/* Education */}
              <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
                <h3 className="text-lg font-semibold text-[#8B0000] font-['Playfair_Display'] mb-4 pb-2 border-b border-gray-100">
                  Education & Career / शिक्षण व करिअर
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
                  <EditableField label="शिक्षण" value={editing ? editData.qualification : profile?.qualification} field="qualification" editing={editing} onChange={handleFieldChange} options={['10th', '12th', 'Diploma', 'Graduate', 'Post Graduate', 'PhD', 'ITI', 'Other']} />
                  <EditableField label="व्यवसाय" value={editing ? editData.occupation : profile?.occupation} field="occupation" editing={editing} onChange={handleFieldChange} />
                  <EditableField label="वार्षिक उत्पन्न" value={editing ? editData.income : profile?.income} field="income" editing={editing} onChange={handleFieldChange} />
                  <EditableField label="कंपनी" value={editing ? editData.company : profile?.company} field="company" editing={editing} onChange={handleFieldChange} />
                </div>
              </div>

              {/* Location */}
              <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
                <h3 className="text-lg font-semibold text-[#8B0000] font-['Playfair_Display'] mb-4 pb-2 border-b border-gray-100">
                  Location / पत्ता
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
                  <EditableField label="शहर" value={editing ? editData.city : profile?.city} field="city" editing={editing} onChange={handleFieldChange} />
                  <EditableField label="राज्य" value={editing ? editData.state : profile?.state} field="state" editing={editing} onChange={handleFieldChange} options={['Maharashtra', 'Karnataka', 'Goa', 'Madhya Pradesh', 'Gujarat', 'Other']} />
                  <EditableField label="सध्याचा पत्ता" value={editing ? editData.currentAddress : profile?.currentAddress} field="currentAddress" editing={editing} onChange={handleFieldChange} />
                  <EditableField label="मूळ गाव" value={editing ? editData.nativePlace : profile?.nativePlace} field="nativePlace" editing={editing} onChange={handleFieldChange} />
                </div>
              </div>

              {/* About */}
              <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
                <h3 className="text-lg font-semibold text-[#8B0000] font-['Playfair_Display'] mb-4 pb-2 border-b border-gray-100">
                  About / स्वतःबद्दल
                </h3>
                <EditableField label="स्वतःबद्दल लिहा" value={editing ? editData.about : profile?.about} field="about" editing={editing} onChange={handleFieldChange} type="textarea" />
              </div>
            </div>
          )}

          {/* PHOTOS TAB */}
          {activeTab === 'photos' && (
            <div className="space-y-5">
              {/* Upload */}
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-[#8B0000]/30 rounded-xl p-8 text-center cursor-pointer hover:border-[#DC143C] hover:bg-[#DC143C]/5 transition-all"
              >
                <FaCloudUploadAlt className="text-4xl text-[#8B0000] mx-auto mb-3" />
                <p className="text-[#8B0000] font-semibold font-['Noto_Sans_Devanagari']">फोटो अपलोड करा</p>
                <p className="text-xs text-gray-500 mt-1">JPG, PNG - Max 5MB</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePhotoUpload}
                  className="hidden"
                />
              </div>

              {/* Photos Grid */}
              {photos.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {photos.map((photo, idx) => (
                    <div key={idx} className="relative group rounded-xl overflow-hidden shadow-sm border border-gray-100">
                      <img src={photo} alt={`Photo ${idx + 1}`} className="w-full h-40 object-cover" />
                      {idx === 0 && (
                        <div className="absolute top-2 left-2 bg-[#FFD700] text-[#8B0000] text-[10px] font-bold px-2 py-0.5 rounded-full">
                          प्राथमिक
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                        {idx !== 0 && (
                          <button
                            onClick={() => handleSetPrimary(photo, idx)}
                            className="bg-[#FFD700] text-[#8B0000] p-2 rounded-full hover:scale-110 transition-transform"
                            title="Set as primary"
                          >
                            <FaStar />
                          </button>
                        )}
                        <button
                          onClick={() => handleDeletePhoto(photo, idx)}
                          className="bg-red-500 text-white p-2 rounded-full hover:scale-110 transition-transform"
                          title="Delete"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <FaCamera className="text-4xl text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 font-['Noto_Sans_Devanagari']">अजून कोणतेही फोटो नाहीत</p>
                </div>
              )}
            </div>
          )}

          {/* PREFERENCES TAB */}
          {activeTab === 'preferences' && (
            <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
              <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-[#8B0000] font-['Playfair_Display']">
                  Partner Preferences / जोडीदार अपेक्षा
                </h3>
                {editingPrefs ? (
                  <div className="flex gap-2">
                    <button onClick={() => { setEditingPrefs(false); setPrefData(preferences); }} className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-lg text-xs text-gray-600 hover:bg-gray-50">
                      <FaTimes /> रद्द
                    </button>
                    <button onClick={handleSavePreferences} disabled={saving} className="flex items-center gap-1 px-3 py-1.5 bg-[#8B0000] text-white rounded-lg text-xs hover:bg-[#DC143C] disabled:opacity-50">
                      <FaSave /> {saving ? 'सेव्ह...' : 'सेव्ह'}
                    </button>
                  </div>
                ) : (
                  <button onClick={() => setEditingPrefs(true)} className="flex items-center gap-1 px-3 py-1.5 bg-[#8B0000] text-white rounded-lg text-xs hover:bg-[#DC143C]">
                    <FaEdit /> संपादित
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
                <EditableField label="कमीत कमी वय" value={editingPrefs ? prefData.ageMin : preferences?.ageMin} field="ageMin" editing={editingPrefs} onChange={handlePrefChange} type="number" />
                <EditableField label="जास्तीत जास्त वय" value={editingPrefs ? prefData.ageMax : preferences?.ageMax} field="ageMax" editing={editingPrefs} onChange={handlePrefChange} type="number" />
                <EditableField label="कमीत कमी उंची" value={editingPrefs ? prefData.heightMin : preferences?.heightMin} field="heightMin" editing={editingPrefs} onChange={handlePrefChange} />
                <EditableField label="जास्तीत जास्त उंची" value={editingPrefs ? prefData.heightMax : preferences?.heightMax} field="heightMax" editing={editingPrefs} onChange={handlePrefChange} />
                <EditableField label="शिक्षण" value={editingPrefs ? prefData.qualification : preferences?.qualification} field="qualification" editing={editingPrefs} onChange={handlePrefChange} />
                <EditableField label="व्यवसाय" value={editingPrefs ? prefData.occupation : preferences?.occupation} field="occupation" editing={editingPrefs} onChange={handlePrefChange} />
                <EditableField label="जात" value={editingPrefs ? prefData.caste : preferences?.caste} field="caste" editing={editingPrefs} onChange={handlePrefChange} />
                <EditableField label="शहर" value={editingPrefs ? prefData.city : preferences?.city} field="city" editing={editingPrefs} onChange={handlePrefChange} />
                <EditableField label="वैवाहिक स्थिती" value={editingPrefs ? prefData.maritalStatus : preferences?.maritalStatus} field="maritalStatus" editing={editingPrefs} onChange={handlePrefChange} options={['अविवाहित', 'विधवा/विधुर', 'घटस्फोटित', 'कोणतेही']} />
                <EditableField label="कमीत कमी उत्पन्न" value={editingPrefs ? prefData.incomeMin : preferences?.incomeMin} field="incomeMin" editing={editingPrefs} onChange={handlePrefChange} />
                <EditableField label="जास्तीत जास्त उत्पन्न" value={editingPrefs ? prefData.incomeMax : preferences?.incomeMax} field="incomeMax" editing={editingPrefs} onChange={handlePrefChange} />
              </div>
            </div>
          )}

          {/* INTERESTS TAB */}
          {activeTab === 'interests' && (
            <div className="space-y-4">
              <div className="flex gap-2 bg-white rounded-xl p-1 shadow-sm border border-gray-100">
                {[
                  { key: 'sent', label: 'पाठवलेले', count: interests.sent.length },
                  { key: 'received', label: 'आलेले', count: interests.received.length },
                  { key: 'mutual', label: 'जुळलेले', count: interests.mutual.length }
                ].map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setInterestsTab(tab.key)}
                    className={`flex-1 py-2.5 text-sm font-medium rounded-lg transition-colors font-['Noto_Sans_Devanagari'] ${
                      interestsTab === tab.key
                        ? 'bg-[#8B0000] text-white shadow'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {tab.label} ({tab.count})
                  </button>
                ))}
              </div>

              {interests[interestsTab].length === 0 ? (
                <div className="text-center py-16 bg-white rounded-xl shadow-sm">
                  <FaHeart className="text-4xl text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 font-['Noto_Sans_Devanagari']">
                    {interestsTab === 'sent' && 'अजून कोणाला रस पाठवला नाही'}
                    {interestsTab === 'received' && 'अजून कोणाचा रस आलेला नाही'}
                    {interestsTab === 'mutual' && 'अजून कोणाशी जुळले नाही'}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {interests[interestsTab].map((interest, idx) => {
                    const p = interest.profile || interest;
                    return (
                      <div key={idx} className="bg-white rounded-xl shadow-sm p-4 border border-gray-100 flex items-center gap-4">
                        <div className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0 border-2 border-[#FFD700]">
                          {p.photos?.[0] ? (
                            <img src={p.photos[0]} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-[#8B0000] to-[#DC143C] flex items-center justify-center">
                              <FaUserCircle className="text-white/50 text-2xl" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-[#8B0000] truncate">{p.name}</h4>
                          <p className="text-xs text-gray-500">{p.age ? `${p.age} वर्षे` : ''} {p.city ? `| ${p.city}` : ''}</p>
                          {interest.status && (
                            <span className={`text-xs mt-1 inline-block px-2 py-0.5 rounded-full ${
                              interest.status === 'accepted' ? 'bg-green-100 text-green-700' :
                              interest.status === 'rejected' ? 'bg-red-100 text-red-700' :
                              'bg-yellow-100 text-yellow-700'
                            }`}>
                              {interest.status === 'accepted' ? 'स्वीकारले' : interest.status === 'rejected' ? 'नाकारले' : 'प्रतीक्षेत'}
                            </span>
                          )}
                        </div>
                        <Link to={`/profile/${p._id}`} className="text-[#8B0000] hover:text-[#DC143C]">
                          <FaChevronRight />
                        </Link>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* SUBSCRIPTION TAB */}
          {activeTab === 'subscription' && (
            <div className="space-y-5">
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isPremium ? 'bg-[#FFD700]' : 'bg-gray-200'}`}>
                    <FaCrown className={`text-xl ${isPremium ? 'text-[#8B0000]' : 'text-gray-400'}`} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#8B0000] font-['Playfair_Display']">
                      {subscription?.plan || (isPremium ? 'Premium' : 'Free')} Plan
                    </h3>
                    <p className="text-sm text-gray-500">
                      {isPremium
                        ? `वैध: ${subscription?.expiresAt ? new Date(subscription.expiresAt).toLocaleDateString('mr-IN') : 'Active'}`
                        : 'मोफत योजना'
                      }
                    </p>
                  </div>
                </div>

                {isPremium ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-green-700 text-sm font-['Noto_Sans_Devanagari'] flex items-center gap-2">
                      <FaCheckCircle /> तुम्ही Premium सदस्य आहात. सर्व सुविधा उपलब्ध आहेत.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="bg-[#FFF8F0] border border-[#FFD700]/30 rounded-lg p-4">
                      <p className="text-[#8B0000] text-sm font-['Noto_Sans_Devanagari'] mb-3">
                        Premium सदस्यता घेऊन अधिक सुविधा मिळवा:
                      </p>
                      <ul className="space-y-2 text-sm text-gray-600">
                        {['अमर्यादित प्रोफाइल पहा', 'चॅट सुविधा', 'WhatsApp नोटिफिकेशन', 'संपर्क माहिती पहा', 'प्राधान्य सूचीत दिसा'].map(feature => (
                          <li key={feature} className="flex items-center gap-2 font-['Noto_Sans_Devanagari']">
                            <FaCheckCircle className="text-[#FFD700] text-xs flex-shrink-0" /> {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <Link
                      to="/pricing"
                      className="block w-full bg-gradient-to-r from-[#8B0000] to-[#DC143C] text-white text-center py-3 rounded-xl font-bold hover:shadow-lg transition-all font-['Noto_Sans_Devanagari']"
                    >
                      <FaCrown className="inline mr-2" /> Premium अपग्रेड करा
                    </Link>
                  </div>
                )}
              </div>

              {/* Verification Link */}
              <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FaShieldAlt className="text-[#8B0000] text-xl" />
                    <div>
                      <h4 className="font-semibold text-gray-800 font-['Noto_Sans_Devanagari']">ओळख पडताळणी</h4>
                      <p className="text-xs text-gray-500">
                        {profile?.isVerified ? 'तुमचे प्रोफाइल सत्यापित आहे' : 'तुमचे प्रोफाइल सत्यापित करा'}
                      </p>
                    </div>
                  </div>
                  <Link
                    to="/aadhaar-verification"
                    className={`px-4 py-2 rounded-lg text-sm font-medium ${
                      profile?.isVerified
                        ? 'bg-green-100 text-green-700'
                        : 'bg-[#8B0000] text-white hover:bg-[#DC143C]'
                    }`}
                  >
                    {profile?.isVerified ? 'सत्यापित' : 'पडताळणी करा'}
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default MyProfilePage;
